import { useRef } from 'react';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  id?: string;
  ariaLabel?: string;
  className?: string;
  onFilterClick?: () => void;
  filterActive?: boolean;
  autoFocus?: boolean;
}

export default function SearchInput({
  value,
  onChange,
  onSubmit,
  placeholder = 'Search…',
  id,
  ariaLabel,
  className = '',
  onFilterClick,
  filterActive = false,
  autoFocus = false,
}: SearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSubmit) onSubmit();
  };

  return (
    <div className={`flex items-stretch gap-2 w-full ${className}`}>
      {/* Input wrapper */}
      <div
        className="relative flex-1 flex items-center"
        style={{
          background: '#ffffff',
          border: '1.5px solid #D1D5DB',
          borderRadius: '8px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          transition: 'border-color 0.15s, box-shadow 0.15s',
        }}
        onFocusCapture={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.borderColor = '#9AA8B8';
          el.style.boxShadow = '0 0 0 3px rgba(148,163,184,0.15)';
        }}
        onBlurCapture={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.borderColor = '#D1D5DB';
          el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)';
        }}
      >
        {/* Magnifying glass — inside input, left side */}
        <span
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center w-4 h-4"
          aria-hidden="true"
        >
          <i
            className="ri-search-line"
            style={{ fontSize: '1rem', color: '#9AA8B8', lineHeight: 1 }}
          />
        </span>

        <input
          ref={inputRef}
          id={id}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={ariaLabel ?? placeholder}
          autoFocus={autoFocus}
          className="w-full bg-transparent pl-9 pr-4 focus:outline-none"
          style={{
            height: '44px',
            fontSize: '0.875rem',
            color: '#1E293B',
            fontFamily: "'Inter', sans-serif",
            caretColor: '#1E293B',
          }}
        />

        {/* Clear button */}
        {value && (
          <button
            type="button"
            onClick={() => { onChange(''); inputRef.current?.focus(); }}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-colors flex-shrink-0"
            style={{ background: '#E2E8F0', color: '#64748B' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#CBD5E1'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#E2E8F0'; }}
          >
            <i className="ri-close-line" style={{ fontSize: '0.7rem', lineHeight: 1 }} aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Optional filter toggle button */}
      {onFilterClick && (
        <button
          type="button"
          onClick={onFilterClick}
          aria-label="Toggle filters"
          aria-pressed={filterActive}
          className="flex items-center justify-center flex-shrink-0 cursor-pointer transition-all"
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '8px',
            background: filterActive ? '#1E293B' : '#ffffff',
            border: `1.5px solid ${filterActive ? '#1E293B' : '#D1D5DB'}`,
            color: filterActive ? '#ffffff' : '#475569',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}
          onMouseEnter={(e) => {
            if (!filterActive) {
              (e.currentTarget as HTMLButtonElement).style.borderColor = '#9AA8B8';
            }
          }}
          onMouseLeave={(e) => {
            if (!filterActive) {
              (e.currentTarget as HTMLButtonElement).style.borderColor = '#D1D5DB';
            }
          }}
        >
          <i className="ri-equalizer-2-line" style={{ fontSize: '1.1rem', lineHeight: 1 }} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
